/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import translate from 'services/i18n/translate';
import { Spinner, Button } from 'ui/components';
import { StartUpRoutePath } from 'routes/paths';
import { Ducks, XError } from '@wit/vtv-x-foundation-lib';
import { addInitializationErrorEvent } from 'store/ducks/analytics/systemEvent';
import { ButtonType } from 'ui/components/Button';

let loginRequestSent = false; // flag used to ignore previous state errors in the login duck

function renderErrorMessage(error?: XError, isLoggingIn?: boolean): JSX.Element | null {
  if (!error || isLoggingIn || !loginRequestSent) {
    return null;
  }

  return (
    <div id="Login_errorMessage" className="vtw-message vtw-icon vtw-error margin-top-10">
      {translate.formatString(translate.error_WA1G219, { error_code: 'WA1G219' }).toString()}
    </div>
  );
}

export default function Code(): JSX.Element {
  const [code, setCode] = useState('');

  const dispatch = useDispatch();
  const isAuthenticated = useSelector(Ducks.Credentials.Selectors.isAuthenticated);
  const isLoggingIn = useSelector(Ducks.Credentials.Selectors.isCredentialsProcessing);
  const loginError = useSelector(Ducks.Credentials.Selectors.getError);

  const routerHistory = useHistory();

  useEffect(() => {
    if (loginError && loginRequestSent) {
      addInitializationErrorEvent(loginError);
    }
  }, [loginError]);

  const handleLogin = () => {
    if (!isAuthenticated && code !== '') {
      dispatch(Ducks.Credentials.Creators.loginCode(code));
      loginRequestSent = true;
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      routerHistory.replace(StartUpRoutePath.ROOT);
    }
  }, [routerHistory, isAuthenticated]);

  // TODO Change dummy data
  const codeMaxLength = 15;

  return (
    <div id="LoginCode_container" className="authentication log-in-code overlay">
      <div className="vtw-back-container margin-top-70 margin-bottom-22">
        <a id="LoginCode_back" onClick={routerHistory.goBack}>
          <span className="vtw-back-nav">{translate.back}</span>
        </a>
      </div>

      <h1 id="LoginCode_title">{translate.logInWithCode}</h1>

      <div className="vtw-container-upper-input">
        {renderErrorMessage(loginError, isLoggingIn)}
        <div className="vtw-container-spinner">
          <Spinner isActive={isLoggingIn} />
        </div>
      </div>

      <div className="vtw-container-input">
        <input
          id="LoginCode_input"
          type="text"
          name="code"
          disabled={isLoggingIn}
          maxLength={codeMaxLength}
          placeholder={translate.logInCodePlaceholder}
          autoComplete="off"
          onChange={(ev) => setCode(ev.target.value)}
        />
      </div>

      <div className="vtw-container-buttons margin-top-80 margin-bottom-64">
        <Button
          bemBlock="LoginCode"
          bemModifier="signIn"
          buttonType={ButtonType.BUTTON}
          disabled={isLoggingIn || !code}
          text={translate['signin.buttonLabel']}
          onClick={handleLogin}
        />
      </div>

      <div className="vtw-message centered margin-bottom-124">{translate.logInCodeExplanatoryText}</div>
    </div>
  );
}
